import { useEffect } from "react";

import { ManualProvider } from "@vericus/cadmus-manual";

import NiceModal from "@ebay/nice-modal-react";
import { useCurrentUser } from "data/user/useCurrentUser";
import { Outlet } from "react-router-dom";

import { CODEX_ENDPOINT, DEPLOY_ENV } from "@/config";
import { useAppDispatch } from "@/data/hooks";
import { hydrateTaskState } from "@/data/task";
import { useGlobalParams } from "@/router/routing";
import { useExposedLocation } from "@/router/useExposedLocation";
import { Toaster } from "@/ui/notifications/Toaster";

import { ApiError } from "../errors";
import { useAppParams } from "./use-app-params";
import { useJitsuIdentify } from "./useJitsuIdentify";

/**
 * Top level route component.
 * At this point, we are working in the realms of an assessment.
 */
export function App() {
  const params = useAppParams();

  // Capture and store the global Tenant and Assessment context via route params
  const { tenant, assessmentId } = useGlobalParams();

  // Identify the current user with Jitsu context
  useJitsuIdentify(assessmentId, tenant);

  // Add route information to the body tag.
  useExposedLocation();

  // Hydrate the Redux Store
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(hydrateTaskState({ assessmentId }));
  }, [assessmentId, dispatch]);

  const { user, loading, error } = useCurrentUser();

  if (loading) return null;

  // If the user is missing OR error exists
  if (error || !user?.me) {
    return <ApiError />;
    // return <Navigate to="/goodbye" replace />;
  }

  return (
    <NiceModal.Provider>
      <ManualProvider
        logger={() => {}} // TODO: should we use logger for Athena?
        user={user?.me}
        referencingStyle={""}
        endpoint={CODEX_ENDPOINT}
        env={DEPLOY_ENV}
        tenant={patchManualTenant(params.tenant)}
        portalId="5206287"
      >
        <>
          <Toaster />
          <Outlet />
        </>
      </ManualProvider>
    </NiceModal.Provider>
  );
}

// Hard mapping of all cadmus-like and sandpit tenant names to proper production
// tenant names for valid Cadmus manual articles. Not all demo and sandpit
// tenants have Manual articles.
// XXX This should be done on the server CODEX
const patchManualTenant = (tenant: string): string =>
  /^cadmus/gi.test(tenant) ? "cadmus" : tenant.replace(/_?sandpit/, "");
